import { backendApi as api } from '../backend/api-slice';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    initializeShoppingCart: build.mutation<InitializeShoppingCartApiResponse, InitializeShoppingCartApiArg>({
      query: (queryArg) => ({
        url: `/api/${queryArg.partnerId}/shopping-cart`,
        method: 'POST',
        body: queryArg.initializeShoppingCartRequest,
      }),
    }),
    submitCart: build.mutation<SubmitCartApiResponse, SubmitCartApiArg>({
      query: (queryArg) => ({
        url: `/api/${queryArg.partnerId}/shopping-cart/${queryArg.cartId}/submit`,
        method: 'POST',
        body: queryArg.submitShoppingCartRequest,
      }),
    }),
    logout: build.mutation<LogoutApiResponse, LogoutApiArg>({
      query: () => ({ url: `/api/auth/logout`, method: 'POST' }),
    }),
    getVersion: build.query<GetVersionApiResponse, GetVersionApiArg>({
      query: () => ({ url: `/info/version` }),
    }),
    getCommitTime: build.query<GetCommitTimeApiResponse, GetCommitTimeApiArg>({
      query: () => ({ url: `/info/time` }),
    }),
    getCommitHash: build.query<GetCommitHashApiResponse, GetCommitHashApiArg>({
      query: () => ({ url: `/info/hash` }),
    }),
    getDetails: build.query<GetDetailsApiResponse, GetDetailsApiArg>({
      query: () => ({ url: `/info/details` }),
    }),
    getProducts: build.query<GetProductsApiResponse, GetProductsApiArg>({
      query: (queryArg) => ({
        url: `/api/${queryArg.partnerId}/product`,
        params: { billingAccountId: queryArg.billingAccountId, servicePointId: queryArg.servicePointId },
      }),
    }),
    getCustomerTvOffer: build.query<GetCustomerTvOfferApiResponse, GetCustomerTvOfferApiArg>({
      query: (queryArg) => ({
        url: `/api/${queryArg.partnerId}/offer/tv`,
        params: { billingAccountId: queryArg.billingAccountId, servicePointId: queryArg.servicePointId },
      }),
    }),
    getCustomerPurchaseEligibility: build.query<
      GetCustomerPurchaseEligibilityApiResponse,
      GetCustomerPurchaseEligibilityApiArg
    >({
      query: (queryArg) => ({
        url: `/api/${queryArg.partnerId}/customer/${queryArg.billingAccountId}/purchase-eligibility`,
        params: { offerId: queryArg.offerId },
      }),
    }),
    getShopStatus: build.query<GetShopStatusApiResponse, GetShopStatusApiArg>({
      query: () => ({ url: `/api/shop/status` }),
    }),
    getPartners: build.query<GetPartnersApiResponse, GetPartnersApiArg>({
      query: () => ({ url: `/api/partner/available` }),
    }),
    getCustomerAccount: build.query<GetCustomerAccountApiResponse, GetCustomerAccountApiArg>({
      query: () => ({ url: `/api/account` }),
    }),
    getAccountInfo: build.query<GetAccountInfoApiResponse, GetAccountInfoApiArg>({
      query: (queryArg) => ({
        url: `/api/account/info`,
        params: { partnerId: queryArg.partnerId, billingAccountId: queryArg.billingAccountId },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as backendApi };
export type InitializeShoppingCartApiResponse = /** status 200 OK */ ShoppingCart;
export type InitializeShoppingCartApiArg = {
  partnerId: string;
  initializeShoppingCartRequest: InitializeShoppingCartRequest;
};
export type SubmitCartApiResponse = /** status 200 OK */ SubmitShoppingCartResponse;
export type SubmitCartApiArg = {
  partnerId: string;
  cartId: string;
  submitShoppingCartRequest: SubmitShoppingCartRequest;
};
export type LogoutApiResponse = unknown;
export type LogoutApiArg = void;
export type GetVersionApiResponse = /** status 200 OK */ string;
export type GetVersionApiArg = void;
export type GetCommitTimeApiResponse = /** status 200 OK */ string;
export type GetCommitTimeApiArg = void;
export type GetCommitHashApiResponse = /** status 200 OK */ string;
export type GetCommitHashApiArg = void;
export type GetDetailsApiResponse = /** status 200 OK */ VersionInfo;
export type GetDetailsApiArg = void;
export type GetProductsApiResponse = /** status 200 OK */ ProductResponse;
export type GetProductsApiArg = {
  partnerId: string;
  billingAccountId: string;
  servicePointId: string;
};
export type GetCustomerTvOfferApiResponse = /** status 200 OK */ TvOfferResponseWithStatus;
export type GetCustomerTvOfferApiArg = {
  partnerId: string;
  billingAccountId: string;
  servicePointId: string;
};
export type GetCustomerPurchaseEligibilityApiResponse = /** status 200 OK */ PurchaseEligibilityResponse;
export type GetCustomerPurchaseEligibilityApiArg = {
  partnerId: string;
  billingAccountId: string;
  offerId: string;
};
export type GetShopStatusApiResponse = /** status 200 OK */ ShopStatus;
export type GetShopStatusApiArg = void;
export type GetPartnersApiResponse = /** status 200 OK */ Partner[];
export type GetPartnersApiArg = void;
export type GetCustomerAccountApiResponse = /** status 200 OK */ Account[];
export type GetCustomerAccountApiArg = void;
export type GetAccountInfoApiResponse = /** status 200 OK */ AccountInfo;
export type GetAccountInfoApiArg = {
  partnerId: string;
  billingAccountId: string;
};
export type Prices = {
  monthlyPrice: number;
  onceOnlyPrice: number;
};
export type ShoppingCart = {
  shoppingCartId: string;
  totalPrices: Prices;
};
export type InitializeShoppingCartRequest = {
  servicePointId: string;
  billingAccountId: string;
  offerId: string;
  numberOfResourcesToAdd?: number;
  resourcesToUpgrade?: string[];
};
export type SubmitShoppingCartResponse = {
  orderNumber: string;
  requestedCompletionDate: string;
};
export type CustomerInformation = {
  firstName: string;
  lastName: string;
};
export type DeliveryType = 'HELTHJEM_IN_SHOP' | 'HELTHJEM_TO_DOOR';
export type SubmitShoppingCartRequest = {
  customerInformation: CustomerInformation;
  addressId: string;
  deliveryType: DeliveryType;
};
export type BuildInfo = {
  group?: string;
  artifact?: string;
  version?: string;
  buildTime?: string;
};
export type GitInfo = {
  branch?: string;
  shortCommitId?: string;
  fullCommitId?: string;
  modified?: boolean;
  commitTime?: string;
  buildTime?: string;
  commitCount?: number;
};
export type VersionInfo = {
  nodeName?: string;
  startupTime?: string;
  buildInfo?: BuildInfo;
  gitInfo?: GitInfo;
};
export type TvOfferResponseStatus =
  | 'SUCCESS'
  | 'TV_OFFER_NOT_AVAILABLE'
  | 'CUSTOMER_ON_WRONG_TV_PLATFORM'
  | 'MULTIPLE_TV_OFFERS'
  | 'NO_BROADBAND_OFFER';
export type NewDecoder = {
  monthlyPrice: number;
  onceOnlyPrice: number;
};
export type Equipment = {
  specification?: string;
  specificationId?: string;
  category?: string;
  mac: string;
  serialNumber?: string;
  model?: string;
  vendor?: string;
  serviceState?: string;
};
export type UpgradeDecoder = {
  monthlyPrice: number;
  onceOnlyPrice: number;
  upgradableUnits: Equipment[];
};
export type DeliveryPricing = {
  deliveryType: DeliveryType;
  price: number;
};
export type Products = {
  offerId: string;
  serviceId: string;
  characteristicId: string;
  maxNumberOfUnits: number;
  numberOfOwnedUnits: number;
  newDecoder?: NewDecoder;
  upgradeDecoder?: UpgradeDecoder;
  deliveryPricing: DeliveryPricing[];
};
export type ProductResponse = {
  status: TvOfferResponseStatus;
  products?: Products;
};
export type OfferResponse = {
  offerId: string;
  serviceId: string;
  characteristicId: string;
  numberOfOwnedStbs: number;
};
export type TvOfferResponseWithStatus = {
  status?: TvOfferResponseStatus;
  response?: OfferResponse;
};
export type PurchaseEligibilityStatus = 'SUCCESS' | 'PURCHASE_RESTRICTION' | 'OPEN_ORDER' | 'DUNNING' | 'USER_ROLE';
export type PurchaseEligibilityResponse = {
  status: PurchaseEligibilityStatus;
};
export type ShopStatus = {
  enabled: boolean;
};
export type Partner = {
  id: string;
  name: string;
  siteUrl?: string;
  logoUrl?: string;
};
export type Account = {
  name?: string;
  customerId: string;
  partnerId: string;
};
export type UserDetails = {
  firstName: string;
  lastName: string;
  partnerNumber: number;
  customerNumber?: string;
  billingAccountId: string;
  crmId?: string;
  crmSystem?: string;
};
export type Address = {
  id: string;
  streetAddress: string;
  streetAddress2?: string;
  postalCode?: string;
  dwellingUnitNumber?: string;
  city?: string;
  country?: string;
};
export type ServiceLocation = {
  siteId: number;
  integrationId: string;
  address: Address;
  services: string[];
};
export type AccountInfo = {
  userDetails: UserDetails;
  serviceLocations: ServiceLocation[];
};
export const {
  useInitializeShoppingCartMutation,
  useSubmitCartMutation,
  useLogoutMutation,
  useGetVersionQuery,
  useGetCommitTimeQuery,
  useGetCommitHashQuery,
  useGetDetailsQuery,
  useGetProductsQuery,
  useGetCustomerTvOfferQuery,
  useGetCustomerPurchaseEligibilityQuery,
  useGetShopStatusQuery,
  useGetPartnersQuery,
  useGetCustomerAccountQuery,
  useGetAccountInfoQuery,
} = injectedRtkApi;
